import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { 
  Plus, 
  Building2, 
  Home, 
  Store, 
  ArrowRight,
  Bot,
  Sparkles,
  Landmark
} from "lucide-react";

const PROPERTY_TYPES = [
  { value: "apartment", label: "Apartment Building", icon: Building2 },
  { value: "house", label: "House", icon: Home },
  { value: "commercial", label: "Commercial Building", icon: Store },
];

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  }).format(value);
};

export default function RealEstate() {
  const { api } = useAuth();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiAddress, setAiAddress] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    property_type: "",
    address: "",
    purchase_price: "",
    current_value: "",
    purchase_date: new Date().toISOString().split('T')[0],
    description: "",
    // Mortgage fields
    has_mortgage: false,
    mortgage_amount: "",
    mortgage_interest_rate: "",
    mortgage_term_years: "30",
    mortgage_start_date: new Date().toISOString().split('T')[0],
    // Insurance and Tax fields
    property_insurance: "",
    property_tax: "",
    hoa_fee: ""
  });

  const fetchProperties = useCallback(async () => {
    try {
      const res = await api.get("/properties");
      setProperties(res.data);
    } catch (error) {
      toast.error("Failed to fetch properties");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchProperties();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const propertyData = {
        name: formData.name,
        property_type: formData.property_type,
        address: formData.address,
        purchase_price: parseFloat(formData.purchase_price),
        current_value: parseFloat(formData.current_value),
        purchase_date: formData.purchase_date,
        description: formData.description || "",
        has_mortgage: formData.has_mortgage,
        // Insurance and Tax fields
        property_insurance: formData.property_insurance ? parseFloat(formData.property_insurance) : null,
        property_tax: formData.property_tax ? parseFloat(formData.property_tax) : null,
        hoa_fee: formData.hoa_fee ? parseFloat(formData.hoa_fee) : null,
      };
      
      // Add mortgage fields if has_mortgage is true
      if (formData.has_mortgage) {
        propertyData.mortgage_amount = parseFloat(formData.mortgage_amount);
        propertyData.mortgage_interest_rate = parseFloat(formData.mortgage_interest_rate);
        propertyData.mortgage_term_years = parseInt(formData.mortgage_term_years);
        propertyData.mortgage_start_date = formData.mortgage_start_date;
      }
      
      await api.post("/properties", propertyData);
      toast.success("Property added successfully");
      setDialogOpen(false);
      setFormData({
        name: "",
        property_type: "",
        address: "",
        purchase_price: "",
        current_value: "",
        purchase_date: new Date().toISOString().split('T')[0],
        description: "",
        has_mortgage: false,
        mortgage_amount: "",
        mortgage_interest_rate: "",
        mortgage_term_years: "30",
        mortgage_start_date: new Date().toISOString().split('T')[0],
        property_insurance: "",
        property_tax: "",
        hoa_fee: ""
      });
      fetchProperties();
    } catch (error) {
      toast.error("Failed to add property");
    }
  };

  const handleAiResearch = async () => {
    if (!aiAddress.trim()) {
      toast.error("Please enter an address");
      return;
    }
    setAiLoading(true);
    try {
      const res = await api.post("/ai/property-research", null, {
        params: { address: aiAddress }
      });
      setAiResult(res.data.analysis);
    } catch (error) {
      toast.error("AI research failed. Please try again.");
    } finally {
      setAiLoading(false);
    }
  };

  const totalValue = properties.reduce((sum, p) => sum + p.current_value, 0);
  const totalPurchase = properties.reduce((sum, p) => sum + p.purchase_price, 0);
  const totalGain = totalValue - totalPurchase;

  return (
    <Layout title="Real Estate">
      <div className="space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="total-properties-card">
            <CardContent className="p-4">
              <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">Properties</p>
              <p className="text-3xl font-bold text-white font-mono">{properties.length}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="total-value-card">
            <CardContent className="p-4">
              <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">Total Value</p>
              <p className="text-3xl font-bold text-amber-500 font-mono">{formatCurrency(totalValue)}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="total-invested-card">
            <CardContent className="p-4">
              <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">Total Invested</p>
              <p className="text-3xl font-bold text-white font-mono">{formatCurrency(totalPurchase)}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="total-gain-card">
            <CardContent className="p-4">
              <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">Total Gain</p>
              <p className={`text-3xl font-bold font-mono ${totalGain >= 0 ? 'text-primary' : 'text-red-500'}`}>
                {totalGain >= 0 ? '+' : ''}{formatCurrency(totalGain)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-3">
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-primary text-black font-bold uppercase tracking-wide rounded-sm hover:bg-primary/90"
                data-testid="add-property-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Property
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-white/10 rounded-none max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-white" style={{ fontFamily: 'Manrope' }}>
                  Add New Property
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Property Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Downtown Apartment Complex"
                    required
                    data-testid="property-name-input"
                    className="bg-black/50 border-white/10 rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Property Type</Label>
                  <Select
                    value={formData.property_type}
                    onValueChange={(value) => setFormData({ ...formData, property_type: value })}
                    required
                  >
                    <SelectTrigger className="bg-black/50 border-white/10 rounded-sm" data-testid="property-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      {PROPERTY_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            <type.icon className="w-4 h-4" />
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Address</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="123 Main St, City, State ZIP"
                    required
                    data-testid="property-address-input"
                    className="bg-black/50 border-white/10 rounded-sm"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Purchase Price</Label>
                    <Input
                      type="number"
                      value={formData.purchase_price}
                      onChange={(e) => setFormData({ ...formData, purchase_price: e.target.value })}
                      placeholder="0"
                      required
                      data-testid="property-purchase-price-input"
                      className="bg-black/50 border-white/10 rounded-sm font-mono"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Current Value</Label>
                    <Input
                      type="number"
                      value={formData.current_value}
                      onChange={(e) => setFormData({ ...formData, current_value: e.target.value })}
                      placeholder="0"
                      required
                      data-testid="property-current-value-input"
                      className="bg-black/50 border-white/10 rounded-sm font-mono"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Purchase Date</Label>
                  <Input
                    type="date"
                    value={formData.purchase_date}
                    onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
                    data-testid="property-purchase-date-input"
                    className="bg-black/50 border-white/10 rounded-sm"
                  />
                </div>
                
                {/* Mortgage Section */}
                <div className="p-4 bg-white/5 border border-white/10 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Landmark className="w-4 h-4 text-violet-500" />
                      <Label className="text-zinc-300">Has Mortgage?</Label>
                    </div>
                    <Switch
                      checked={formData.has_mortgage}
                      onCheckedChange={(checked) => setFormData({ ...formData, has_mortgage: checked })}
                      data-testid="has-mortgage-toggle"
                    />
                  </div>
                  
                  {formData.has_mortgage && (
                    <div className="space-y-4 pt-2 border-t border-white/10">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Loan Amount</Label>
                          <Input
                            type="number"
                            value={formData.mortgage_amount}
                            onChange={(e) => setFormData({ ...formData, mortgage_amount: e.target.value })}
                            placeholder="0"
                            required={formData.has_mortgage}
                            data-testid="mortgage-amount-input"
                            className="bg-black/50 border-white/10 rounded-sm font-mono"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Interest Rate (%)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={formData.mortgage_interest_rate}
                            onChange={(e) => setFormData({ ...formData, mortgage_interest_rate: e.target.value })}
                            placeholder="6.5"
                            required={formData.has_mortgage}
                            data-testid="mortgage-rate-input"
                            className="bg-black/50 border-white/10 rounded-sm font-mono"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Loan Term</Label>
                          <Select
                            value={formData.mortgage_term_years}
                            onValueChange={(value) => setFormData({ ...formData, mortgage_term_years: value })}
                          >
                            <SelectTrigger className="bg-black/50 border-white/10 rounded-sm" data-testid="mortgage-term-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0A0A0A] border-white/10">
                              <SelectItem value="10">10 Years</SelectItem>
                              <SelectItem value="15">15 Years</SelectItem>
                              <SelectItem value="20">20 Years</SelectItem>
                              <SelectItem value="25">25 Years</SelectItem>
                              <SelectItem value="30">30 Years</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Start Date</Label>
                          <Input
                            type="date"
                            value={formData.mortgage_start_date}
                            onChange={(e) => setFormData({ ...formData, mortgage_start_date: e.target.value })}
                            data-testid="mortgage-start-date-input"
                            className="bg-black/50 border-white/10 rounded-sm"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                <Button
                  type="submit"
                  className="w-full bg-primary text-black font-bold uppercase tracking-wide rounded-sm hover:bg-primary/90"
                  data-testid="submit-property-btn"
                >
                  Add Property
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={aiDialogOpen} onOpenChange={(open) => {
            if (!aiLoading) setAiDialogOpen(open);
          }}>
            <DialogTrigger asChild>
              <Button 
                variant="outline"
                className="border-violet-500/50 text-violet-400 hover:bg-violet-500/10 rounded-sm"
                data-testid="ai-research-btn"
              >
                <Bot className="w-4 h-4 mr-2" />
                AI Property Research
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-violet-500/30 rounded-none max-w-2xl">
              <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <Sparkles className="w-5 h-5 text-violet-500" />
                  AI Property Research
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Enter Address to Research</Label>
                  <div className="flex gap-2">
                    <Input
                      value={aiAddress}
                      onChange={(e) => setAiAddress(e.target.value)}
                      placeholder="123 Main St, City, State ZIP"
                      data-testid="ai-address-input"
                      className="bg-black/50 border-white/10 rounded-sm flex-1"
                    />
                    <Button
                      onClick={handleAiResearch}
                      disabled={aiLoading}
                      className="bg-violet-500 text-white hover:bg-violet-600 rounded-sm"
                      data-testid="ai-research-submit-btn"
                    >
                      {aiLoading ? "Analyzing..." : "Research"}
                    </Button>
                  </div>
                </div>
                {aiResult && (
                  <div className="p-4 bg-violet-500/10 border border-violet-500/30 rounded-sm max-h-96 overflow-y-auto">
                    <p className="text-xs font-mono uppercase tracking-widest text-violet-400 mb-2">AI Analysis</p>
                    <div className="text-sm text-zinc-300 whitespace-pre-wrap">{aiResult}</div>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Properties Grid */}
        {loading ? (
          <div className="text-center py-12 text-zinc-500">Loading properties...</div>
        ) : properties.length === 0 ? (
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardContent className="p-12 text-center">
              <Building2 className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
              <p className="text-zinc-400 mb-4">No properties yet. Add your first property!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {properties.map((property) => {
              const typeInfo = PROPERTY_TYPES.find(t => t.value === property.property_type);
              const Icon = typeInfo?.icon || Building2;
              const gain = property.current_value - property.purchase_price;
              const gainPercent = ((gain / property.purchase_price) * 100).toFixed(1);
              
              return (
                <Link to={`/real-estate/${property.id}`} key={property.id}>
                  <Card 
                    className="bg-[#0A0A0A] border-white/10 rounded-none card-hover cursor-pointer h-full"
                    data-testid={`property-card-${property.id}`}
                  >
                    <CardContent className="p-0">
                      {/* Property image placeholder */}
                      <div className="h-40 bg-gradient-to-br from-amber-500/20 to-amber-600/5 flex items-center justify-center border-b border-white/10">
                        <Icon className="w-16 h-16 text-amber-500/50" />
                      </div>
                      <div className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h3 className="font-bold text-white text-lg" style={{ fontFamily: 'Manrope' }}>
                              {property.name}
                            </h3>
                            <p className="text-sm text-zinc-500">{property.address}</p>
                          </div>
                          <span className="text-xs px-2 py-1 bg-white/5 text-zinc-400 uppercase font-mono">
                            {typeInfo?.label || property.property_type}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-4 mt-4">
                          <div>
                            <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Value</p>
                            <p className="text-xl font-bold text-amber-500 font-mono">
                              {formatCurrency(property.current_value)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Gain</p>
                            <p className={`text-xl font-bold font-mono ${gain >= 0 ? 'text-primary' : 'text-red-500'}`}>
                              {gain >= 0 ? '+' : ''}{gainPercent}%
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-end mt-4 text-sm text-zinc-500">
                          <span>View Details</span>
                          <ArrowRight className="w-4 h-4 ml-1" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
